/**
 * TPG Platform — Brand Asset Manager (frontend).
 *
 * Platform-neutral resolution. Components request dotted registry keys only —
 * never hard-code Brand Package filenames.
 *
 *   BrandAssetManager.get('ratings.active')
 *   BrandAssetManager.get('content.featured')
 *
 * Bootstrap: configData.brand_assets (assets map + active package metadata).
 * Format-agnostic: SVG, PNG, or future formats — consumers never branch on extension.
 */

import configuration from "react-global-configuration";

var ALIASES = {
  "ratings.inactive": "ratings.icon",
  "ratings.off": "ratings.icon",
  "ratings.on": "ratings.active",
  "ratings.filled": "ratings.active",
  "favorites.icon": "navigation.favorites",
  "watch_later.icon": "navigation.watch_later",
  "history.icon": "navigation.history",
  "verified.badge": "creator.verified",
  "creator.verified_badge": "creator.verified",
  "creator.featured_badge": "creator.featured",
  "featured.badge": "content.featured",
  "featured.icon": "content.featured",
  "originals.badge": "content.originals",
  "originals.icon": "content.originals",
  "premium.badge": "content.premium",
  "premium.icon": "content.premium",
  "exclusive.badge": "content.exclusive",
  "live.badge": "content.live",
  "coming_soon.badge": "content.coming_soon",
  "new_release.badge": "content.new_release",
  "new.badge": "content.new_release",
  "staff_pick.badge": "community.staff_pick",
  "editors_choice.badge": "community.editors_choice",
  "trending.badge": "community.trending",
  "recommended.badge": "community.recommended",
};

/** Registry key → family.package alias from brand_assets bootstrap. */
var FAMILY_ALIAS = {
  "ratings.icon": "outline",
  "ratings.active": "gold",
};

/**
 * When brand_assets bootstrap is missing icon URLs, fall back to known
 * same-origin package filenames (format-agnostic: SVG/PNG via <img>).
 * Package id comes from brand_assets.package.id or BE default pentecost-plus.
 */
var DEFAULT_PACKAGE_ID = "pentecost-plus";
var DEFAULT_PACKAGE_FILES = {
  "ratings.icon": "ratings-dove-outline.svg",
  "ratings.active": "ratings-dove-gold.svg",
};

/** Platform default when Brand Package omits labels.collection. */
var DEFAULT_COLLECTION_LABEL = "My Favorites";

function normalizeKey(key) {
  var k = String(key || "").trim();
  if (!k) return "";
  if (ALIASES[k]) return ALIASES[k];
  return k;
}

function readBrandAssets() {
  try {
    return configuration.get("configData.brand_assets") || {};
  } catch (e) {
    return {};
  }
}

function activePackageId() {
  var brand = readBrandAssets();
  var id = brand && brand.package && brand.package.id;
  if (id) return String(id).trim();
  try {
    var fromConfig = String(
      configuration.get("configData.brand_package") || ""
    ).trim();
    if (fromConfig) return fromConfig;
  } catch (e) {
    /* ignore */
  }
  return DEFAULT_PACKAGE_ID;
}

/**
 * Rewrite absolute backend package URLs to same-origin root-relative paths.
 * Split FE/BE deploys 404 absolute API-host URLs; both hosts serve
 * /brand-packages/{id}/assets/{file}. Custom uploads (S3/http) are left alone.
 */
function toConsumableUrl(url) {
  var raw = String(url || "").trim();
  if (!raw) return "";
  var match = raw.match(/\/brand-packages\/[^/?#]+\/assets\/[^/?#]+/);
  if (match) return match[0];
  return raw;
}

/**
 * Same-origin package URL built from bootstrap package id + filename.
 * Falls back to DEFAULT_PACKAGE_FILES for ratings when registry URLs are empty.
 */
function sameOriginPackageUrl(registryKey) {
  var brand = readBrandAssets();
  var pkg = activePackageId();
  if (!pkg) return "";

  var assets = (brand && brand.assets) || {};
  var absolute = assets[registryKey] ? String(assets[registryKey]) : "";
  var file = "";
  if (absolute) {
    var parts = absolute.split("/");
    file = parts[parts.length - 1].split("?")[0];
  }
  if (!file) {
    var family = (brand.family && brand.family.package) || {};
    var alias = FAMILY_ALIAS[registryKey];
    if (alias && family[alias]) {
      var famParts = String(family[alias]).split("/");
      file = famParts[famParts.length - 1].split("?")[0];
    }
  }
  if (!file && DEFAULT_PACKAGE_FILES[registryKey]) {
    file = DEFAULT_PACKAGE_FILES[registryKey];
  }
  if (!file) return "";
  return "/brand-packages/" + pkg + "/assets/" + file;
}

function lookup(key) {
  var brand = readBrandAssets();
  var assets = (brand && brand.assets) || {};
  if (assets[key]) return toConsumableUrl(assets[key]);

  var family = (brand.family && brand.family.package) || {};
  var alias = FAMILY_ALIAS[key];
  if (alias && family[alias]) return toConsumableUrl(family[alias]);

  if (key === "ratings.icon") {
    try {
      var off = String(configuration.get("configData.ratings_icon") || "").trim();
      if (off) return toConsumableUrl(off);
      off = String((brand.ratings && brand.ratings.icon) || "").trim();
      if (off) return toConsumableUrl(off);
    } catch (e) {
      /* ignore */
    }
  }
  if (key === "ratings.active") {
    try {
      var on = String(
        configuration.get("configData.ratings_icon_active") || ""
      ).trim();
      if (on) return toConsumableUrl(on);
      on = String((brand.ratings && brand.ratings.icon_active) || "").trim();
      if (on) return toConsumableUrl(on);
    } catch (e) {
      /* ignore */
    }
  }

  return sameOriginPackageUrl(key);
}

/**
 * Resolve a registry key to a consumable URL (any image format).
 * @param {string} key
 * @param {{ mode?: string, theme?: string, seasonal?: string }} [context]
 * @returns {string}
 */
export function get(key, context) {
  var normalized = normalizeKey(key);
  if (!normalized) return "";
  void context;
  return lookup(normalized);
}

/**
 * Ratings inactive + active pair for BrandRating.
 * Primary URLs are same-origin when possible; fallbacks cover split deploys.
 * @returns {{ off: string, on: string, offFallback: string, onFallback: string }}
 */
export function ratingsIcons() {
  var offFallback = sameOriginPackageUrl("ratings.icon");
  var onFallback = sameOriginPackageUrl("ratings.active") || offFallback;
  var off = get("ratings.icon") || offFallback;
  var on = get("ratings.active") || onFallback || off;
  // Prefer same-origin package paths when primary still points at empty/broken hosts.
  if (!off) off = offFallback;
  if (!on) on = onFallback || off;
  return {
    off: off,
    on: on,
    offFallback: offFallback && offFallback !== off ? offFallback : "",
    onFallback: onFallback && onFallback !== on ? onFallback : "",
  };
}

export function siteConfig() {
  return readBrandAssets();
}

export function brandGold() {
  var brand = readBrandAssets();
  if (brand && brand.accent) return brand.accent;
  if (brand && brand.brand_gold) return brand.brand_gold;
  if (brand && brand.package && brand.package.accent) return brand.package.accent;
  return "#C9A227";
}

export function activePackage() {
  var brand = readBrandAssets();
  return (brand && brand.package) || null;
}

/**
 * Brand-specific collection / wishlist label (SPA CTA + section headings).
 * Resolution order:
 *   1. configData.brand_assets.package.labels.collection (Brand Package)
 *   2. configData.brand_assets.labels.collection
 *   3. configData.collection_label (top-level bootstrap mirror)
 *   4. Platform default "My Favorites"
 * Prefer this over i18n "my_list" for user-facing collection copy.
 * @returns {string}
 */
export function collectionLabel() {
  var brand = readBrandAssets();
  var pkg = (brand && brand.package) || {};
  var labels = pkg.labels || brand.labels || {};
  var label = String(
    labels.collection || labels.collection_label || ""
  ).trim();
  if (label) return label;
  try {
    label = String(
      configuration.get("configData.collection_label") || ""
    ).trim();
    if (label) return label;
  } catch (e) {
    /* ignore */
  }
  return DEFAULT_COLLECTION_LABEL;
}

var BrandAssetManager = {
  get: get,
  ratingsIcons: ratingsIcons,
  siteConfig: siteConfig,
  brandGold: brandGold,
  activePackage: activePackage,
  activePackageId: activePackageId,
  collectionLabel: collectionLabel,
  normalizeKey: normalizeKey,
  sameOriginPackageUrl: sameOriginPackageUrl,
  toConsumableUrl: toConsumableUrl,
};

export default BrandAssetManager;
export { BrandAssetManager };
